import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
  Icon,
  Divider,
  Link,
  Image
} from '@chakra-ui/react';
import { 
  FaServer,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaArrowLeft,
  FaCheckCircle
} from 'react-icons/fa';
import apiService from '../services/api';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Extract token and user_id from URL parameters
  const token = searchParams.get('token');
  const userId = searchParams.get('user_id');

  // Color mode values
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  useEffect(() => {
    // Check if required parameters are present
    if (!token || !userId) {
      setError('Invalid or missing reset link parameters. Please request a new password reset.');
    }
  }, [token, userId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!formData.password.trim()) {
      setError('Password is required');
      return false;
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    if (!token || !userId) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const resetData = {
        user_id: parseInt(userId),
        password: formData.password,
        token: token
      };

      await apiService.resetPassword(resetData);
      
      setSuccess(true);
      
      toast({
        title: 'Password Reset Successful',
        description: 'Your password has been updated successfully. You can now log in with your new password.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Failed to reset password:', error);
      setError('Failed to reset password. The link may have expired or is invalid. Please request a new password reset.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  if (success) {
    return (
      <Box minH="100vh" bg={bg} display="flex" alignItems="center" justifyContent="center">
        <Container maxW="md">
          <Card bg={cardBg} shadow="lg" borderRadius="xl">
            <CardBody p={8}>
              <VStack spacing={6} textAlign="center">
                <Icon as={FaCheckCircle} color="green.500" boxSize={16} />
                <Heading size="lg" color="green.600">
                  Password Reset Successful!
                </Heading>
                <Text color="gray.600">
                  Your password has been updated successfully. You will be redirected to the login page shortly.
                </Text>
                <Button
                  colorScheme="brand"
                  size="lg"
                  onClick={handleBackToLogin}
                  w="full"
                >
                  Go to Login
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={bg}>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
        <Container maxW="7xl" py={4}>
        <HStack 
              spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')} 
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >

            <Image
              src="/simple-logo.png"
              alt="SRS Logo"
              boxSize={14}
              objectFit="contain"
            />
            <Box>
              <Heading size="lg" color="brand.500">SRS</Heading>
              <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
            </Box>
          </HStack>
        </Container>
      </Box>

      {/* Reset Password Form */}
      <Box display="flex" alignItems="center" justifyContent="center" minH="calc(100vh - 100px)">
        <Container maxW="md">
          <Card bg={cardBg} shadow="lg" borderRadius="xl">
            <CardBody p={8}>
              <VStack spacing={6}>
                <VStack spacing={2} textAlign="center">
                  <Icon as={FaKey} color="brand.500" boxSize={12} />
                  <Heading size="lg" color="gray.800">
                    Reset Your Password
                  </Heading>
                  <Text color="gray.600">
                    Enter your new password below
                  </Text>
                </VStack>

                <Divider />

                {error && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel color="gray.700" fontWeight="semibold">New Password</FormLabel>
                      <InputGroup>
                        <Input
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Enter your new password"
                          borderColor="gray.300"
                          _hover={{ borderColor: "gray.400" }}
                          _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                            onClick={() => setShowPassword(!showPassword)}
                            variant="ghost"
                            size="sm"
                          />
                        </InputRightElement>
                      </InputGroup>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Password must be at least 8 characters long
                      </Text>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel color="gray.700" fontWeight="semibold">Confirm New Password</FormLabel>
                      <InputGroup>
                        <Input
                          name="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          placeholder="Confirm your new password"
                          borderColor="gray.300"
                          _hover={{ borderColor: "gray.400" }}
                          _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                            icon={showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            variant="ghost"
                            size="sm"
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    <Button
                      type="submit"
                      colorScheme="brand"
                      size="lg"
                      w="full"
                      isLoading={isSubmitting}
                      loadingText="Resetting Password..."
                      isDisabled={!token || !userId}
                    >
                      Reset Password
                    </Button>
                  </VStack>
                </form>

                <Divider />

                <HStack spacing={2} justify="center">
                  <Link
                    onClick={handleBackToLogin}
                    color="brand.500"
                    fontSize="sm"
                    cursor="pointer"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    <HStack spacing={1}>
                      <Icon as={FaArrowLeft} boxSize={3} />
                      <Text>Back to Login</Text>
                    </HStack>
                  </Link>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default ResetPassword;
